import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  Tooltip,
  Box,
  Input,
  InputAdornment,
  Container,
  Dialog,
  DialogActions,
  DialogTitle,
  Checkbox,
  DialogContent,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import mammoth from "mammoth";
import { ReactComponent as UploadIcon } from "../../../images/uploadIcon.svg";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import { ReactComponent as ImportIcon } from "../../../images/importIcon.svg";
import { ReactComponent as ConfigureIcon } from "../../../images/parameterIcon.svg";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { ReactComponent as LoadIcon } from "../../../images/loadExampleButton.svg";
import ValidationPopup from "../../popup-components/ValidationPopup";
import {
  extractFigmaFileKeyAPI,
  extractVideoTextAPI,
  validateUserInput,
  validCandidateUserStory,
} from "../../../services/webServices";
import {
  extractTextAPI,
  freeFlowInvest,
  investigate,
  modifyUserStoryData,
  extractTextFromImageAPI,
  userstoryEvaluation,
} from "../../../services/webServices";
import {
  LoadingModalContext,
  MessagePopupContext,
} from "../../main-pages/ServicePage";
import {
  UserStoryFlowDataContext,
  UserStoryLoaderStateContext,
} from "../../main-pages/UserStoryMainPage";
import { ReactComponent as AutomateIcon } from "../../../images/automateIcon.svg";
import FullscreenToggle from "../../custom-components/FullScreenToggle";
import CommonAccordion from "../../custom-components/CommonAccordion";
import SessionExpired from "../../popup-components/SessionExpiredPopup";
import ALMConnectionPage from "../../main-pages/input-pages/ALMConnection/ALMConnectionPage";
import * as XLSX from "xlsx"; // Import for Excel parsing
import UserStoriesUpload from "./UserStoriesUpload";
import UploadPanel from "./UploadPanel";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";

const StepItem = ({ imgSrc, text, isActive, showArrow }) => (
  <Box display="flex" alignItems="center">
    <img src={imgSrc} alt="" className="step-icon" />
    {showArrow && (
      <ArrowForwardIosIcon
        fontSize="small"
        sx={{ opacity: 0.5, fontSize: "12px" }}
      />
    )}
    <Typography
      variant="body1"
      className={`step-text ${isActive ? "active" : ""}`}
    >
      {text}
    </Typography>
  </Box>
);

const StepsWithDropdown = () => {
  const steps = [
    {
      imgSrc:
        "https://cdn.builder.io/api/v1/image/assets/TEMP/911db717332ff445c6c88c44ed82fc3e19bd7216e7cbe68eeb282f2ea6b23256?placeholderIfAbsent=true&apiKey=9384815652264ccaa6717f615f796430",
      text: "Enter Details",
      isActive: true,
      showArrow: true,
    },
    {
      imgSrc:
        "https://cdn.builder.io/api/v1/image/assets/TEMP/4845d5450893eb5648e1f7dbdbd0dd99793a7f74d5e441c211c448cea6509310?placeholderIfAbsent=true&apiKey=9384815652264ccaa6717f615f796430",
      text: "Evaluate User Stories",
      isActive: false,
      showArrow: false,
    },
    {
      imgSrc:
        "https://cdn.builder.io/api/v1/image/assets/TEMP/cd3f0e0e1836fdfc16da42ab1b97402cb9c75181c3a93aff5c063af7a3928a84?placeholderIfAbsent=true&apiKey=9384815652264ccaa6717f615f796430",
      text: "Refine User Stories",
      isActive: false,
      showArrow: false,
    },
    {
      imgSrc:
        "https://cdn.builder.io/api/v1/image/assets/TEMP/6e7d946cba76312f3da062d3c5c9dbc707e8efd2e4a3be69dd3a534c93fc86fc?placeholderIfAbsent=true&apiKey=9384815652264ccaa6717f615f796430",
      text: "Generated Use Cases",
      isActive: false,
      showArrow: false,
    },
    {
      imgSrc:
        "https://cdn.builder.io/api/v1/image/assets/TEMP/6cc46303d75f92c8315f090f2f96cd9d41e0892cf4ca39b1345d03692bb0d586?placeholderIfAbsent=true&apiKey=9384815652264ccaa6717f615f796430",
      text: "Generated Test Cases",
      isActive: false,
      showArrow: false,
    },
  ];

  return (
    <Box className="steps-with-dropdown">
      {steps.map((step, index) => (
        <StepItem key={index} {...step} />
      ))}
      <style jsx>{`
        .steps-with-dropdown {
          display: flex;
          align-items: center;
          gap: 8px;
          overflow: hidden;
          color: var(--Schemes-Secondary, #000);
          text-align: center;
          letter-spacing: 0.5px;
          justify-content: start;
          font: 400 16px Manrope, sans-serif;
        }
        .step-icon {
          width: 18px;
          height: 18px;
          object-fit: contain;
          object-position: center;
        }
        .step-text {
          opacity: 0.5;
        }
        .step-text.active {
          opacity: 1;
        }
      `}</style>
    </Box>
  );
};

const defaultParameters = [
  "Independent",
  "Negotiable",
  "Valuable",
  "Estimable",
  "Small",
  "Testable",
];

const MainUserStoryFormInput = () => {
  const { setUserStoryLoaderState } = useContext(UserStoryLoaderStateContext);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [acceptanceCriteria, setAcceptanceCriteria] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [textFieldError, setTextFieldError] = useState(false);
  const [freeFlowUserStory, setFreeFlowUserStory] = useState("");
  const [openValidationPopup, setOpenValidationPopup] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [openUploadPanel, setOpenUploadPanel] = useState(false);

  const [selectedOptions, setSelectedOptions] = useState({
    Independent: true,
    Negotiable: true,
    Valuable: true,
    Estimable: true,
    Small: true,
    Testable: true,
  });
  const [reqData, setReqData] = useState({});
  const [reportData, setReportData] = useState(null);
  const [responseError, setResponseError] = useState(null);
  const [investData, setInvestData] = useState({
    userStory: "",
    parameters: defaultParameters,
  });
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  const [sessionExpiredValue, setSessionExpiredValue] = useState(false);
  const [openProjectPage, setOpenProjectPage] = useState(false);
  const [openUploadUserStory, setOpenUploadUserStory] = useState(false);
  const [uploadedUserStories, setUploadedUserStories] = useState([]);
  const { setMessagePopup, setMessage } = useContext(MessagePopupContext);

  const fileInputRef = useRef(null);

  const handleUploadClick = () => {
    // if (fileInputRef.current) {
    //   fileInputRef.current.click();
    // }
    setOpenUploadPanel(true);
  };

  const handleAccordionChange = (event, expanded) => {
    setIsAccordionOpen(expanded); // Set the button's visibility based on accordion state
  };

  const examples = {
    LoadExample: {
      title: "Add a New Contact",
      description:
        "As a user, I want to add new contact information to the CRM system so that I can manage and store details for my business relationships efficiently.",
      acceptanceCriteria:
        "1. The system should provide a form with fields for: \n- First Name (mandatory)\n- Middle Name (optional)\n- Last Name (mandatory)\n- Company (optional)\n- Email (optional).\n2. Mandatory fields should be validated before saving.\n3. After saving, the new contact should appear in the contact list with accurate details.",
      additionalInfo:
        "Domain Name: Contact Management \nModule Name: Contacts \nType of User: CRM users (Admin, Sales, Marketing) \nPriority: High",
    },
  };

  const selectLoadExample = (event) => {
    event.stopPropagation();
    setTitle(examples["LoadExample"]["title"]);
    setDescription(examples["LoadExample"]["description"]);
    setAcceptanceCriteria(examples["LoadExample"]["acceptanceCriteria"]);
    setAdditionalInfo(examples["LoadExample"]["additionalInfo"]);
    setTextFieldError(false);
  };

  const { setLoadingState } = useContext(LoadingModalContext);
  const {
    setUserStory,
    setEvaluationSummary,
    setRefinedUserStory,
    setUseCase,
  } = useContext(UserStoryFlowDataContext);

  const navigate = useNavigate();

  useEffect(() => {
    setUserStory({});
    setEvaluationSummary([]);
    setRefinedUserStory({});
    setUseCase({});
  }, []);

  const isUserStoryFilled = freeFlowUserStory.trim();
  const isDetailsFilled =
    title.trim() && description.trim() && acceptanceCriteria.trim();

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setSelectedOptions((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  const handleSettingsClick = () => {
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  const handleFormSubmit = async () => {
    const checkedParameters = Object.keys(selectedOptions).filter(
      (key) => selectedOptions[key] === true // Only include checked options
    );
    const requestData = {
      data: `Title: ${title},
Description: ${description},
Acceptance Criteria: ${acceptanceCriteria},
Additional Information: ${additionalInfo}`,
      parameters: checkedParameters,
    };

    setUserStoryLoaderState("Evaluation Summary");
    setUserStory(requestData);
    let progressHandler;
    try {
      progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 15000,
      });
      progressHandler.start("Evaluating User Story");

      // Call userInvest for core evaluation check
      const userInvestResponse = await validateUserInput(requestData);
      const userInvestResult = userInvestResponse.data?.Result?.toLowerCase();

      if (userInvestResult !== "clean") {
        const errorMsg =
          userInvestResponse?.data?.message ||
          userInvestResponse?.data?.Reason ||
          "Inappropriate user story detected. Please revise and try again.";
        setMessageType("profanity");
        setOpenValidationPopup(true);
        progressHandler.stop();
        return;
      }

      progressHandler.updateMessage("Evaluating User Story");
      // All clean — proceed with freeFlowInvest
      const response = await userstoryEvaluation(requestData);
      const parsedInvestResponse = response.data.evaluation;
      const isRAGavaiable = response.data.isRAGavaiable;

      await progressHandler.finishAndClose();
      navigate("/dashboard/userstory/output", {
        state: {
          userStory: {
            Title: title,
            Description: description,
            "Acceptance Criteria": acceptanceCriteria,
            "Additional Information": additionalInfo,
            parameters: checkedParameters,
          },
          evaluationSummary: parsedInvestResponse,
          refinedUserStory: {},
          useCase: {},
          isRAGavaiable: isRAGavaiable,
        },
      });
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.Reason ||
        "An error occurred.";
      setResponseError(errorMsg);

      // Show popup with error
      setMessage(errorMsg);
      setMessagePopup(true);

      if (errorMsg === "Your session has expired.") {
        setSessionExpiredValue(true);
      }
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      if (progressHandler) {
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const handleConfirmClick = () => {
    const isAnySelected = Object.values(selectedOptions).some((value) => value);

    if (!isAnySelected) {
      alert("Please select at least one parameter.");
    } else {
      handleSettingsClose();
    }
  };

  const handleSumbitFreeFlowText = async () => {
    setUserStory(freeFlowUserStory);
    setUserStoryLoaderState("Evaluation Summary");

    const checkedParameters = Object.keys(selectedOptions).filter(
      (key) => selectedOptions[key]
    );

    const requestData = {
      data: freeFlowUserStory,
      parameters: checkedParameters,
    };
    setReqData(requestData);
    let progressHandler;

    try {
      progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 15000,
      });
      progressHandler.start("Validating the input");

      // Call userInvest for core evaluation check
      const userInvestResponse = await validateUserInput(requestData);
      const userInvestResult = userInvestResponse.data?.Result?.toLowerCase();

      if (userInvestResult !== "clean") {
        const errorMsg =
          userInvestResponse?.data?.message ||
          userInvestResponse?.data?.Reason ||
          "Inappropriate user story detected. Please revise and try again.";
        setMessageType("profanity");
        setOpenValidationPopup(true);
        progressHandler.stop();
        return;
      }

      progressHandler.updateMessage("Evaluating User Story");
      // All clean — proceed with freeFlowInvest
      const freeFlowResponse = await userstoryEvaluation(requestData);
      const parsedInvestResponse = freeFlowResponse.data.evaluation;
      const isRAGavaiable = freeFlowResponse.data.isRAGavaiable;

      setEvaluationSummary(parsedInvestResponse);
      await progressHandler.finishAndClose();

      navigate("/dashboard/userstory/output", {
        state: {
          userStory: freeFlowUserStory,
          evaluationSummary: parsedInvestResponse,
          refinedUserStory: {},
          useCase: {},
          isRAGavaiable: isRAGavaiable,
        },
      });
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.Error ||
        error?.response?.data?.Reason ||
        "Something went wrong. Please try again.";

      setResponseError(errorMsg);
      setMessage(errorMsg);
      setMessagePopup(true);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      if (progressHandler) {
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const handleFileUpload = (event) => {
    event.preventDefault();
    setUploadedUserStories([]);
    setOpenUploadUserStory(false);
    setLoadingState({
      open: true,
      message: "Uploading your input file",
      progress: null,
    });

    const file = event.target.files[0];
    if (!file) return;

    const sizeInMB = file.size / (1024 * 1024);
    if (sizeInMB > 50) {
      setOpenValidationPopup(true);
      setMessageType("File Size");
      setLoadingState({ open: false, message: "", progress: null });
      return;
    }

    const extension = file.name.split(".").pop().toLowerCase();
    const reader = new FileReader();

    reader.onload = async (e) => {
      const arrayBuffer = e.target.result;

      try {
        switch (extension) {
          case "xlsx":
          case "xls":
            await handleExcelUpload(arrayBuffer);
            break;

          case "docx":
            await handleDocxUpload(arrayBuffer);
            break;

          case "pdf":
            await handlePdfUpload(arrayBuffer);
            break;

          case "mp3":
          case "wav":
          case "aac":
          case "m4a":
            await handleAudioUpload(file);
            break;
          case "mp4":
          case "avi":
          case "mkv":
            await handleVideoUpload(file);
            break;

          case "png":
          case "jpg":
            await handleImageUpload(file);
            break;

          default:
            setOpenValidationPopup(true);
            setMessageType("Unsupported format");
        }
      } catch (err) {
        console.log("err", err);
        if (err?.response?.data?.message === "Your session has expired.") {
          setSessionExpiredValue(true);
        } else {
          setMessageType("Error");
          setOpenValidationPopup(true);
        }
      } finally {
        event.target.value = null;
        setLoadingState({ open: false, message: "", progress: null });
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleUploadPanelSubmit = async ({ figmaUrl, file }) => {
    setLoadingState({
      open: true,
      message: "Uploading your input file",
      progress: null,
    });
    if (figmaUrl) {
      // Handle figma URL case here, you can add a handler like handleFigmaUrlUpload(figmaUrl)
      handleFigmaUrlSubmit(figmaUrl);
    } else if (file) {
      // Simulate an event for handleFileUpload
      const simulatedEvent = {
        preventDefault: () => {},
        target: { files: [file], value: null },
      };

      handleFileUpload(simulatedEvent);
    }
  };

  // Excel Handler - Read all sheets
  const handleExcelUpload = async (arrayBuffer) => {
    try {
      const data = new Uint8Array(arrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });

      let allSheetData = [];

      workbook.SheetNames.forEach((sheetName) => {
        const sheet = workbook.Sheets[sheetName];

        // SOLUTION: Manually read the worksheet range to get ALL columns
        const range = XLSX.utils.decode_range(sheet["!ref"]);
        const numCols = range.e.c - range.s.c + 1; // Total number of columns
        const numRows = range.e.r - range.s.r + 1; // Total number of rows

        // Manually extract ALL rows including headers
        const sheetRows = [];
        for (let R = range.s.r; R <= range.e.r; ++R) {
          const row = [];
          for (let C = range.s.c; C <= range.e.c; ++C) {
            const cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })];
            const cellValue = cell ? XLSX.utils.format_cell(cell) : "";
            row.push(cellValue);
          }
          sheetRows.push(row);
        }

        if (sheetRows.length === 0) return;

        const [headerRow, ...dataRows] = sheetRows;

        // Check if first row looks like headers
        const structuredKeys = [
          "description",
          "title",
          "acceptance criteria",
          "additional information",
        ];

        const looksStructured = headerRow.some(
          (cell) =>
            typeof cell === "string" &&
            structuredKeys.some((key) => cell.toLowerCase().includes(key))
        );

        if (looksStructured) {
          // Create proper headers for ALL columns
          const headers = headerRow.map((h, index) => {
            if (!h || h.trim() === "") {
              return `column_${index}`;
            }
            return String(h).trim().toLowerCase().replace(/\s+/g, "_");
          });

          dataRows.forEach((row) => {
            const isEmptyRow = row.every(
              (cell) => !cell || String(cell).trim() === ""
            );
            if (isEmptyRow) return;

            const rowObject = {};
            headers.forEach((key, idx) => {
              const cellValue = row[idx] ? String(row[idx]).trim() : "";
              if (cellValue) {
                rowObject[key] = cellValue;
              }
            });

            if (Object.keys(rowObject).length > 0) {
              allSheetData.push(rowObject);
            }
          });
        } else {
          // Fallback for fully unstructured sheet
          sheetRows.forEach((row) => {
            const fullText = row.filter(Boolean).join(" ").trim();
            if (fullText) {
              allSheetData.push({ userStory: fullText });
            }
          });
        }
      });

      if (allSheetData.length === 0) {
        setMessageType("Invalid User Story");
        setOpenValidationPopup(true);
        return;
      }

      // Create combined text from ALL fields
      // const plainText = allSheetData
      //   .map((obj) => Object.values(obj).join(" - "))
      //   .join("\n\n");

      const jsonData = JSON.stringify(allSheetData);

      const validUserStory = await validCandidateUserStory({
        userStory: jsonData,
      });

      const status = validUserStory.data?.Status;
      const isValid =
        status?.trim().toLowerCase() === "yes, this is a user story candidate";

      if (!isValid) {
        setMessageType("Invalid User Story");
        setOpenValidationPopup(true);
        return;
      }

      const userStoryData = await modifyUserStoryData(jsonData);

      setUploadedUserStories(userStoryData.data);
      setOpenUploadUserStory(true);
    } catch (error) {
      if (error?.response?.data?.message === "Your session has expired.") {
        setSessionExpiredValue(true);
      } else {
        setMessageType("Error");
        setOpenValidationPopup(true);
      }
    }
  };

  // DOCX Handler
  const handleDocxUpload = async (arrayBuffer) => {
    try {
      const { value } = await mammoth.extractRawText({ arrayBuffer });
      const jsonData = JSON.stringify({ content: value });

      const validUserStory = await validCandidateUserStory({
        userStory: jsonData,
      });

      const status = validUserStory.data?.Status;
      const isValid =
        status?.trim().toLowerCase() === "yes, this is a user story candidate";

      if (!isValid) {
        setMessageType("Invalid User Story");
        setOpenValidationPopup(true);
        return;
      }

      const userStoryData = await modifyUserStoryData(jsonData);

      setUploadedUserStories(userStoryData.data);
      setOpenUploadUserStory(true);
    } catch (error) {
      if (error?.response?.data?.message === "Your session has expired.") {
        setSessionExpiredValue(true);
      } else {
        setMessageType("Error");
        setOpenValidationPopup(true);
      }
    }
  };

  // Audio Handler
  const handleAudioUpload = async (file) => {
    const formData = new FormData();
    formData.append("audio", file);

    try {
      const response = await extractTextAPI(formData);
      const transcribedText = response.data.text;

      const jsonData = JSON.stringify({ content: transcribedText });

      const validUserStory = await validCandidateUserStory({
        userStory: jsonData,
      });

      const status = validUserStory.data?.Status;
      const isValid =
        status?.trim().toLowerCase() === "yes, this is a user story candidate";

      if (!isValid) {
        setMessageType("Invalid User Story");
        setOpenValidationPopup(true);
        return;
      }

      const userStoryData = await modifyUserStoryData(jsonData);

      setUploadedUserStories(userStoryData.data);
      setOpenUploadUserStory(true);
    } catch (error) {
      if (error?.response?.data?.message === "Your session has expired.") {
        setSessionExpiredValue(true);
      } else {
        setMessageType("Error");
        setOpenValidationPopup(true);
      }
    }
  };

  // Video Handler
  const handleVideoUpload = async (file) => {
    const formData = new FormData();
    formData.append("video", file);

    try {
      const response = await extractVideoTextAPI(formData);
      let transcribedText = response.data;

      if (!Array.isArray(transcribedText)) {
        transcribedText = [transcribedText];
      }

      const formattedExtractedStories = transcribedText.map(
        (eachStory) => eachStory["UserStory"]
      );

      const jsonData = JSON.stringify({ content: transcribedText });

      const validUserStory = await validCandidateUserStory({
        userStory: jsonData,
      });

      const status = validUserStory.data?.Status;
      const isValid =
        status?.trim().toLowerCase() === "yes, this is a user story candidate";

      if (!isValid) {
        setMessageType("Invalid User Story");
        setOpenValidationPopup(true);
        return;
      }

      setUploadedUserStories(formattedExtractedStories);
      setOpenUploadUserStory(true);
    } catch (error) {
      setMessageType("Error");
      setOpenValidationPopup(true);
    }
  };

  // Image Handler
  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await extractTextFromImageAPI(formData);
      const resultArray = response.data; // this is the array from Python

      // Extract UserStories from the first element
      const formattedExtractedStories = resultArray[0]?.UserStories || [];

      const jsonData = JSON.stringify({ content: formattedExtractedStories });

      const validUserStory = await validCandidateUserStory({
        userStory: jsonData,
      });

      const status = validUserStory.data?.Status;
      const isValid =
        status?.trim().toLowerCase() === "yes, this is a user story candidate";

      if (!isValid) {
        setMessageType("Invalid User Story");
        setOpenValidationPopup(true);
        return;
      }

      setUploadedUserStories(formattedExtractedStories);
      setOpenUploadUserStory(true);
    } catch (error) {
      if (error?.response?.data?.message === "Your session has expired.") {
        setSessionExpiredValue(true);
      } else {
        setMessageType("Error");
        setOpenValidationPopup(true);
      }
    }
  };

  // PDF Handler
  const handlePdfUpload = async (arrayBuffer) => {
    try {
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = "";

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();

        const pageText = textContent.items.map((item) => item.str).join(" ");
        fullText += pageText + "\n\n";
      }
      console.log("Extracted PDF Text=============================>", fullText);
      const jsonData = JSON.stringify({ content: fullText });

      // const validUserStory = await validCandidateUserStory({
      //   userStory: jsonData,
      // });
      // const status = validUserStory.data?.Status;
      // const isValid =
      //   status?.trim().toLowerCase() === "yes, this is a user story candidate";

      // if (!isValid) {
      //   setMessageType("Invalid User Story");
      //   setOpenValidationPopup(true);
      //   return;
      // }

      const userStoryData = await modifyUserStoryData(jsonData);

      setUploadedUserStories(userStoryData.data);
      setOpenUploadUserStory(true);
    } catch (error) {
      if (error?.response?.data?.message === "Your session has expired.") {
        setSessionExpiredValue(true);
      } else {
        setMessageType("Error");
        setOpenValidationPopup(true);
      }
    }
  };

  // Figma Handler
  const extractFigmaFileKey = (figmaUrl) => {
    const match = figmaUrl.match(
      /figma\.com\/(?:file|design)\/([a-zA-Z0-9]+)\//
    );
    return match ? match[1] : null;
  };

  const handleFigmaUrlSubmit = async (figmaUrl) => {
    setLoadingState({
      open: true,
      message: "Uploading your input file ",
      progress: null,
    });

    const figmaFileKey = extractFigmaFileKey(figmaUrl);

    if (!figmaFileKey) {
      setMessageType("Invalid Figma URL");
      setOpenValidationPopup(true);
      return;
    }

    try {
      // Call your backend to process the Figma design
      const response = await extractFigmaFileKeyAPI({
        figmaFileKey: figmaFileKey,
      });

      const allUserStories = response.data.pages.flatMap((page) => page.layers);
      const jsonData = JSON.stringify({ content: allUserStories });

      const validUserStory = await validCandidateUserStory({
        userStory: jsonData,
      });
      const status = validUserStory.data?.Status;
      const isValid =
        status?.trim().toLowerCase() === "yes, this is a user story candidate";

      if (!isValid) {
        setMessageType("Invalid User Story");
        setOpenValidationPopup(true);
        return;
      }
      setUploadedUserStories(allUserStories);
      setOpenUploadUserStory(true);

      setLoadingState({ open: false, message: "", progress: null });
    } catch (error) {
      if (error?.response?.data?.message === "Your session has expired.") {
        setSessionExpiredValue(true);
      } else {
        setMessageType("Invalid Figma URL");

        setOpenValidationPopup(true);
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const importUserStoriesFormJira = async () => {
    setOpenProjectPage(true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f6f3f2",
        minHeight: "100vh",
        width: "100%",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#888",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#555",
        },
      }}
    >
      <Box
        component="main"
        sx={{
          width: "100%",
          mx: "auto", // center horizontally
          maxWidth: {
            xs: "100%", // mobile
            sm: "100%", // tablet
            md: "95%", // small laptop
            lg: "90%", // desktop
            xl: "100%", // large desktop
          },
          px: { xs: 2, sm: 3, md: 4, lg: 5 }, // responsive padding
          py: 2,
          mb: 10, // extra bottom space for footer
        }}
      >
        <Container maxWidth="md">
          {/* Header Section */}
          <Box
            sx={{
              bgcolor: "transparent",
              mb: 1,
              p: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box display="flex" alignItems="center">
                <AutomateIcon style={{ marginRight: 4 }} />
                <Typography variant="body2" color="text.secondary">
                  Refine user stories to create detailed requirements.
                </Typography>
              </Box>
              {/* <FullscreenToggle /> */}
            </Box>
          </Box>

          {/* User Story Free Text Card */}
          <Card
            sx={{
              bgcolor: "white",
              mb: 2,
              borderRadius: "5px",
              border: "1px solid rgba(0,0,0,0.12)",
              width: "100%",
            }}
          >
            <form>
              <CardContent sx={{ p: 2 }}>
                <Input
                  fullWidth
                  disableUnderline
                  multiline
                  minRows={5}
                  maxRows={10}
                  placeholder="Enter user story details"
                  value={freeFlowUserStory}
                  onChange={(e) => setFreeFlowUserStory(e.target.value)}
                  autoComplete="off"
                  sx={{
                    color: "black",
                    bgcolor: "transparent",
                    p: "10px 10px 10px 33px",
                    zIndex: 1,
                    "&:hover, &:focus": { border: "none" },
                  }}
                />
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    disabled={!isUserStoryFilled}
                    onClick={handleSumbitFreeFlowText}
                    sx={{
                      backgroundColor: isDetailsFilled ? "#49A3FF" : "default",
                      textTransform: "none",
                    }}
                  >
                    Evaluate
                  </Button>
                </Box>
              </CardContent>

              {/* Settings Dialog */}
              <Dialog
                open={settingsOpen}
                onClose={handleSettingsClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                  sx: {
                    width: "600px",
                    maxHeight: "90vh",
                    borderRadius: "6px",
                    p: 2,
                  },
                }}
              >
                <DialogTitle>Configure Parameters</DialogTitle>
                <Typography
                  variant="body1"
                  sx={{
                    pt: 1,
                    fontWeight: 500,
                    color: "#555",
                    fontSize: 14,
                    pl: 3,
                  }}
                >
                  Select Parameters to customise and focus the generated results
                </Typography>
                <DialogContent
                  sx={{ px: 2, display: "flex", flexDirection: "column" }}
                >
                  {[
                    "Independent",
                    "Negotiable",
                    "Valuable",
                    "Estimable",
                    "Small",
                    "Testable",
                  ].map((key) => (
                    <Box key={key} sx={{ mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedOptions[key]}
                            onChange={handleCheckboxChange}
                            name={key}
                            sx={{ "&.Mui-checked": { color: "#49A3FF" } }}
                          />
                        }
                        label={<Typography variant="body2">{key}</Typography>}
                      />
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontSize: "0.8rem", lineHeight: 1.2, ml: 4 }}
                      >
                        {
                          {
                            Independent:
                              "User story should be self-contained...",
                            Negotiable: "User story should be flexible...",
                            Valuable: "User story should provide value...",
                            Estimable: "User story should be estimable...",
                            Small: "User story should be small enough...",
                            Testable: "Ensures the user story can be tested...",
                          }[key]
                        }
                      </Typography>
                    </Box>
                  ))}
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={handleSettingsClose}
                    sx={{ textTransform: "none", color: "#49A3FF" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirmClick}
                    startIcon={<GenerateButton />}
                    sx={{
                      textTransform: "none",
                      backgroundColor: "#49A3FF",
                      color: "white",
                      "&:hover": { backgroundColor: "#3385cc" },
                    }}
                  >
                    Confirm
                  </Button>
                </DialogActions>
              </Dialog>
            </form>
          </Card>

          <Divider
            textAlign="center"
            sx={{
              width: "100%",
              my: 2,
              fontSize: "14px",
              fontWeight: 400,
              color: "#888",
            }}
          >
            OR
          </Divider>

          {/* Accordion for Manual Entry */}
          <CommonAccordion
            summary={
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1" sx={{ fontSize: "16px" }}>
                  Fill the user story details
                </Typography>
                <Button
                  variant="text"
                  startIcon={<LoadIcon />}
                  sx={{ textTransform: "none" }}
                  onClick={selectLoadExample}
                >
                  Load sample text
                </Button>
              </Box>
            }
            details={
              <Box>
                {/* Form fields */}
                <Box
                  sx={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    pr: 1,
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#c1c1c1",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  {[
                    ["Title", title, setTitle],
                    ["Description*", description, setDescription],
                    [
                      "Acceptance Criteria",
                      acceptanceCriteria,
                      setAcceptanceCriteria,
                    ],
                    [
                      "Additional Information*",
                      additionalInfo,
                      setAdditionalInfo,
                    ],
                  ].map(([label, value, setter]) => (
                    <TextField
                      key={label}
                      label={label}
                      placeholder="Enter here"
                      variant="outlined"
                      margin="normal"
                      multiline
                      rows={4}
                      value={value}
                      onChange={(e) => setter(e.target.value)}
                      sx={{
                        mt: 2,
                        width: "100%",
                        "& .MuiInputBase-root textarea": {
                          scrollbarWidth: "thin",
                          "&::-webkit-scrollbar": { width: "6px" },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#ccc",
                            borderRadius: "4px",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: "#f0f0f0",
                          },
                        },
                      }}
                    />
                  ))}
                </Box>

                {/* Divider above buttons */}
                <Divider sx={{ my: 2, width: "100%" }} />

                {/* Buttons row */}
                <Box display="flex" alignItems="center" flexWrap="wrap" pb={2}>
                  <Button
                    variant="text"
                    startIcon={<UploadIcon />}
                    sx={{ textTransform: "none", mr: 2 }}
                    onClick={handleUploadClick}
                  >
                    Upload
                  </Button>
                  <Button
                    variant="text"
                    startIcon={<ImportIcon />}
                    sx={{ textTransform: "none", mr: 2 }}
                    onClick={importUserStoriesFormJira}
                  >
                    Import
                  </Button>

                  {/* Evaluate button pushed to the right with margin from edge */}
                  <Box sx={{ ml: "auto", mr: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleFormSubmit}
                      disabled={!description || !additionalInfo}
                      sx={{
                        textTransform: "none",
                        backgroundColor:
                          description && additionalInfo ? "#49A3FF" : "default",
                      }}
                    >
                      Evaluate
                    </Button>
                  </Box>
                </Box>
              </Box>
            }
            type="input"
          />

          {/* Conditional Pages */}
          {openProjectPage && (
            <ALMConnectionPage
              issueType="User Stories"
              setPageCloser={setOpenProjectPage}
            />
          )}
          {openUploadUserStory && (
            <UserStoriesUpload uploadedUserStories={uploadedUserStories} />
          )}
          {sessionExpiredValue && <SessionExpired />}
          {openValidationPopup && (
            <ValidationPopup
              open={openValidationPopup}
              messageType={messageType}
              onClose={() => setOpenValidationPopup(false)}
            />
          )}
          <UploadPanel
            open={openUploadPanel}
            onClose={() => setOpenUploadPanel(false)}
            onUpload={handleUploadPanelSubmit}
          />
        </Container>
      </Box>

      {/* Fixed Footer */}
      <Box
        component="footer"
        sx={{
          width: "100%",
          // maxWidth: "601px",
          backgroundColor: "#f6f3f2",
          textAlign: "center",
          paddingX: "5px",
          position: "fixed",
          bottom: "0",
          zIndex: "99",
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Use QMentisAI™ with human supervision, as with all generative AI
          tools.
        </Typography>
      </Box>
    </Box>
  );
};

export default MainUserStoryFormInput;
